import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
} from './languages'
import { translatePhrase } from './phraseTranslations'

const TEXT_ORIGINALS = new WeakMap()
const ATTRIBUTE_ORIGINALS = new WeakMap()
const TRANSLATABLE_ATTRIBUTES = ['aria-label', 'alt', 'placeholder', 'title']
const SKIP_SELECTOR =
  '.notranslate, [notranslate], [data-no-translate], script, style, code, pre, textarea'
const ADMIN_PATH_PATTERN = /^\/(admin|qc|fit3d-admin)(\/|$)/

const preserveWhitespace = (source, translated) => {
  const leading = source.match(/^\s*/)[0]
  const trailing = source.match(/\s*$/)[0]

  return `${leading}${translated}${trailing}`
}

const shouldSkipNode = (node) => {
  const parent = node && node.parentElement

  return !parent || !!parent.closest(SKIP_SELECTOR)
}

const translateTextValue = (language, value) => {
  const trimmed = value.replace(/\s+/g, ' ').trim()

  if (!trimmed) {
    return value
  }

  return preserveWhitespace(value, translatePhrase(language, trimmed))
}

const isManagedTranslation = (value, original) =>
  value === original ||
  SUPPORTED_LANGUAGES.some(
    ({ code }) => value === translateTextValue(code, original)
  )

const translateTextNode = (language, node) => {
  if (shouldSkipNode(node)) {
    return
  }

  const existingOriginal = TEXT_ORIGINALS.get(node)
  const currentValue = node.nodeValue
  const original =
    existingOriginal && isManagedTranslation(currentValue, existingOriginal)
      ? existingOriginal
      : currentValue

  TEXT_ORIGINALS.set(node, original)
  const translatedValue =
    language === DEFAULT_LANGUAGE
      ? original
      : translateTextValue(language, original)

  if (node.nodeValue !== translatedValue) {
    node.nodeValue = translatedValue
  }
}

const getAttributeOriginals = (element) => {
  if (!ATTRIBUTE_ORIGINALS.has(element)) {
    ATTRIBUTE_ORIGINALS.set(element, {})
  }

  return ATTRIBUTE_ORIGINALS.get(element)
}

const translateElementAttributes = (language, element) => {
  if (!element || element.closest(SKIP_SELECTOR)) {
    return
  }

  const originals = getAttributeOriginals(element)

  TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) {
      return
    }

    const currentValue = element.getAttribute(attribute)
    const existingOriginal = originals[attribute]
    const original =
      existingOriginal && isManagedTranslation(currentValue, existingOriginal)
        ? existingOriginal
        : currentValue

    originals[attribute] = original
    const translatedValue =
      language === DEFAULT_LANGUAGE
        ? original
        : translateTextValue(language, original)

    if (element.getAttribute(attribute) !== translatedValue) {
      element.setAttribute(attribute, translatedValue)
    }
  })
}

const walkAndTranslate = (language, root) => {
  if (!root || root.closest?.(SKIP_SELECTOR)) {
    return
  }

  if (root.nodeType === window.Node.TEXT_NODE) {
    translateTextNode(language, root)
    return
  }

  if (
    root.nodeType !== window.Node.ELEMENT_NODE &&
    root.nodeType !== window.Node.DOCUMENT_NODE
  ) {
    return
  }

  if (root.nodeType === window.Node.ELEMENT_NODE) {
    translateElementAttributes(language, root)
  }

  const walker = document.createTreeWalker(
    root,
    window.NodeFilter.SHOW_TEXT | window.NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if (
          node.nodeType === window.Node.ELEMENT_NODE &&
          node.closest(SKIP_SELECTOR)
        ) {
          return window.NodeFilter.FILTER_REJECT
        }

        if (node.nodeType === window.Node.TEXT_NODE && shouldSkipNode(node)) {
          return window.NodeFilter.FILTER_REJECT
        }

        return window.NodeFilter.FILTER_ACCEPT
      },
    },
    false
  )

  let node = walker.currentNode
  while (node) {
    if (node.nodeType === window.Node.TEXT_NODE) {
      translateTextNode(language, node)
    } else if (node.nodeType === window.Node.ELEMENT_NODE) {
      translateElementAttributes(language, node)
    }
    node = walker.nextNode()
  }
}

export const shouldTranslatePath = (pathname = '') =>
  !ADMIN_PATH_PATTERN.test(pathname)

export const applyDomTranslations = (language, root = document.body) => {
  const normalizedLanguage = normalizeLanguage(language)

  if (!shouldTranslatePath(window.location.pathname)) {
    walkAndTranslate(DEFAULT_LANGUAGE, root)
    return
  }

  walkAndTranslate(normalizedLanguage, root)
}

export const createDomTranslationObserver = (getLanguage) => {
  let observer
  let applying = false
  let pendingApply = false

  const getEffectiveLanguage = () =>
    shouldTranslatePath(window.location.pathname)
      ? normalizeLanguage(getLanguage())
      : DEFAULT_LANGUAGE

  const schedulePendingApply = () => {
    if (!pendingApply) {
      return
    }

    pendingApply = false
    setTimeout(apply, 0)
  }

  const apply = () => {
    if (!document.body) {
      return
    }

    if (applying) {
      pendingApply = true
      return
    }

    applying = true
    try {
      applyDomTranslations(getLanguage())
    } finally {
      applying = false
      schedulePendingApply()
    }
  }

  return {
    start() {
      apply()

      if (!window.MutationObserver || observer) {
        return
      }

      observer = new window.MutationObserver((mutations) => {
        if (applying) {
          pendingApply = true
          return
        }

        applying = true
        try {
          const language = getEffectiveLanguage()

          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              walkAndTranslate(language, node)
            })

            if (mutation.type === 'characterData') {
              translateTextNode(language, mutation.target)
            }
          })
        } finally {
          applying = false
          schedulePendingApply()
        }
      })

      observer.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true,
      })
    },

    apply,

    stop() {
      if (observer) {
        observer.disconnect()
        observer = null
      }
    },
  }
}
